import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '../constants/apiConfig';

interface UseNotificationSocketOptions {
  enabled?: boolean;
  onNewNotification?: (data: any) => void;
  onNotificationCountUpdate?: (data: { count: number }) => void;
  onNotificationRead?: (data: { notificationId: string }) => void;
  onAllNotificationsRead?: () => void;
}

export function useNotificationSocket({
  enabled = false,
  onNewNotification,
  onNotificationCountUpdate,
  onNotificationRead,
  onAllNotificationsRead
}: UseNotificationSocketOptions) {
  const socketRef = useRef<Socket | null>(null);
  const callbacksRef = useRef({
    onNewNotification,
    onNotificationCountUpdate,
    onNotificationRead,
    onAllNotificationsRead
  });

  useEffect(() => {
    callbacksRef.current = {
      onNewNotification,
      onNotificationCountUpdate,
      onNotificationRead,
      onAllNotificationsRead
    };
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const socket = io(`${SOCKET_URL}/notifications`, {
      transports: ['websocket'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000
    });

    socket.on('connect', () => {
      console.log('notification socket connected to server');
      socket.emit('joinNotificationRoom', {});
    });

    socket.on('newNotification', data => {
      // console.log('New notification received:', data);
      if (callbacksRef.current.onNewNotification) {
        callbacksRef.current.onNewNotification(data.data);
      }
    });

    socket.on('notificationCountUpdate', data => {
      // console.log('Notification count updated:', data);
      if (callbacksRef.current.onNotificationCountUpdate) {
        callbacksRef.current.onNotificationCountUpdate(data.data);
      }
    });

    socket.on('notificationRead', data => {
      // console.log('Notification read:', data);
      if (callbacksRef.current.onNotificationRead) {
        callbacksRef.current.onNotificationRead(data.data);
      }
    });

    socket.on('allNotificationsRead', () => {
      // console.log('All notifications read');
      if (callbacksRef.current.onAllNotificationsRead) {
        callbacksRef.current.onAllNotificationsRead();
      }
    });

    socket.on('disconnect', () => {
      console.log('notification socket disconnected from server');
    });

    socket.on('connect_error', error => {
      console.error('Notification socket connection error:', error);
    });

    socketRef.current = socket;

    return () => {
      socket.off('newNotification');
      socket.off('notificationCountUpdate');
      socket.off('notificationRead');
      socket.off('allNotificationsRead');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled]);

  return socketRef.current;
}
